import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useCreateBlankAIPage, useGeneratePage } from '@dashboard/hooks/queries/useAIQueries';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { aiEditorUrl } from '@dashboard/context/AIContext/aiEditorRoute';

const PAGE_TYPES = ['checkout', 'event', 'form'];

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

// Same rules as generatePageSchema in the API: one bad value in the link must
// not fail the whole page generation.
const THEME_PARAMS = {
  colorPrimary: { param: 'color', pattern: HEX_COLOR },
  radius: { param: 'radius', pattern: /^(none|sm|md|lg|xl|full)$/ },
  colorMode: { param: 'mode', pattern: /^(light|dark)$/ },
  colorSurface: { param: 'surface', pattern: HEX_COLOR },
  fontHeading: { param: 'headingFont', pattern: /^[A-Za-z0-9 ]{1,60}$/ },
};

const loadingMessages = [
  'Analyzing your idea...',
  'Crafting the perfect layout...',
  'Writing compelling copy...',
  'Designing your checkout flow...',
  'Adding finishing touches...',
  'Almost there...',
];

export default function AIGenerate() {
  const [messageIndex, setMessageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { sellerId, token } = useAuth();
  const { mutateAsync: generatePage } = useGeneratePage();
  const { mutateAsync: createBlankPage } = useCreateBlankAIPage();

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(i => {
        // Stop at "Almost there..." (last message)
        if (i >= loadingMessages.length - 1) {
          return i;
        }
        return i + 1;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  function getPrompt() {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('prompt') || localStorage.getItem('cp_ai_prompt');
  }

  function getPageType() {
    const type = new URLSearchParams(location.search).get('type');
    return PAGE_TYPES.includes(type) ? type : undefined;
  }

  function getTheme() {
    const params = new URLSearchParams(location.search);
    const theme = Object.fromEntries(
      Object.entries(THEME_PARAMS)
        .map(([key, { param, pattern }]) => [key, params.get(param), pattern])
        .filter(([, value, pattern]) => value && pattern.test(value))
        .map(([key, value]) => [key, value]),
    );
    return Object.keys(theme).length ? theme : undefined;
  }

  async function generate() {
    const prompt = getPrompt();
    if (!prompt) {
      history.replace('/pages');
      return;
    }

    const type = getPageType();
    const theme = getTheme();
    const collectAddress =
      new URLSearchParams(location.search).get('address') === '0' ? false : undefined;

    try {
      // A blank page needs a type; without one the one-shot create picks it from the prompt.
      if (type) {
        const { pageId } = await createBlankPage({ type, theme, collectAddress });
        localStorage.removeItem('cp_ai_prompt');
        history.replace(aiEditorUrl(pageId), { aiPrompt: prompt });
        return;
      }

      const result = await generatePage({
        description: prompt,
        theme,
        collectAddress,
        sellerId,
        token,
      });

      localStorage.removeItem('cp_ai_prompt');

      history.replace(`/pages/${result.pageId}/customize?ai=1`);
    } catch (err) {
      console.error('Error generating page:', err);
      // fetcher throws the API body; an Error is browser text, e.g. gateway HTML that won't parse.
      const apiMessage = err instanceof Error ? undefined : err?.message;
      setError(apiMessage || 'Something went wrong while creating your page. Please try again.');
    }
  }

  useEffect(() => {
    if (!sellerId || !token || hasRun) {
      return;
    }

    setHasRun(true);
    generate();
  }, [sellerId, token, hasRun]);

  if (error) {
    return (
      <div className="fixed inset-0 z-[100] bg-white dark:bg-background flex flex-col items-center justify-center">
        <div className="flex flex-col items-center max-w-md text-center px-6">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/15 flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-medium text-gray-900 dark:text-foreground mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-gray-500 dark:text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => history.replace('/pages')}
            className="px-4 py-2 bg-gray-900 dark:bg-primary text-white dark:text-primary-foreground rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-primary/90 transition-colors"
          >
            Go to pages
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-white dark:bg-background flex flex-col items-center justify-center">
      <div className="flex flex-col items-center">
        <div className="relative mb-8">
          <SparklesIcon className="w-8 h-8 text-gray-900 dark:text-foreground" />
        </div>

        <h1 className="text-xl font-medium text-gray-900 dark:text-foreground mb-1">
          Creating your page
        </h1>
        <p className="text-sm text-gray-500 dark:text-muted-foreground h-5">
          {loadingMessages[messageIndex]}
        </p>
      </div>
    </div>
  );
}
